import React, { useEffect, useState } from "react";
import { Button, message } from "antd";
import styles from "./index.module.scss";
import TradeModal from "../../../components/TradeModal";
import Service from "../../../services";
import { getUserInfo, EXCHANGE_INSTRUMENT, isSubAccount, UNGROUPED_NAME, errorToString } from "../../../utils";
import GroupTable from "../../../components/accountOverview/GroupTable";
import { MinusSquareOutlined, PlusSquareOutlined } from "@ant-design/icons";

const AccountOverview = ({ setSideBarPage }) => {
  // 被选择的action(trade|pause|empty)所对应的tradeStatus的details
  const [actionModalDetails, setActionModalDetails] = useState(null);
  // 某个group里面有哪些accounts被selected了
  const [popoverSelectedAccountsByGroup, setPopoverSelectedAccountsByGroup] = useState({});
  const [userInfo, setUserInfo] = useState();
  // 某个group里面有哪些tradeAccountStatus
  const [tradeStatusListByGroup, setTradeStatusListByGroup] = useState({});
  const [allTablesExpanded, setAllTablesExpanded] = useState(false);

  useEffect(() => {
    const getUser = async () => {
      try {
        setUserInfo(await getUserInfo());
      } catch (error) {
        message.error(errorToString(error));
      }
    };
    getUser();
  }, []);

  useEffect(() => {
    userInfo && fetchTradeStatusAndPermissions();
  }, [userInfo]);

  // 轮询
  useEffect(() => {
    const timer = setInterval(() => {
      userInfo && fetchTradeStatusAndPermissions();
    }, 15 * 1000); // 轮询interval
    return () => clearInterval(timer);
    // Each time the state updates, clear the previous timer and rebuild a new one
  }, [tradeStatusListByGroup, userInfo]);

  const fetchTradeStatusAndPermissions = async () => {
    try {
      const listManagedTradeAccountStatus = await Service.listManagedTradeAccountStatus();
      const tradeStatusListByGroup = {};
      listManagedTradeAccountStatus.forEach(({ groupName, parsedTradeAccountStatusList }) => {
        // do not display algo accounts (spot algo and future algo)
        tradeStatusListByGroup[groupName] = parsedTradeAccountStatusList.filter(
          (status) =>
            status.permissions.includes("READ") &&
            // TODO: fix this after enable spot trading
            ![EXCHANGE_INSTRUMENT.binance_spot_algo, EXCHANGE_INSTRUMENT.okx_spot_algo, EXCHANGE_INSTRUMENT.binance_future_algo].includes(
              status.exchange
            )
        );
      });
      setTradeStatusListByGroup(tradeStatusListByGroup);

      const popoverSelectedAccountsByGroup = { [UNGROUPED_NAME]: [] }; //在EditGroup popover里面有哪些账户默认被选择了
      for (let group in tradeStatusListByGroup) {
        if (group === UNGROUPED_NAME) continue;
        // 使用Set去掉重复的exchangeAccount
        const exchangeAccountSet = new Set(
          tradeStatusListByGroup[group].filter((status) => !isSubAccount(status)).map((status) => status.exchangeAccount)
        );
        exchangeAccountSet.delete(EXCHANGE_INSTRUMENT.binance_spot_algo); // do not display binance_spot_algo
        exchangeAccountSet.delete(EXCHANGE_INSTRUMENT.okx_spot_algo); // do not display okx_spot_algo
        exchangeAccountSet.delete(EXCHANGE_INSTRUMENT.binance_future_algo); // do not display binance_future_algo
        popoverSelectedAccountsByGroup[group] = [...exchangeAccountSet];
      }

      setPopoverSelectedAccountsByGroup((prevSelectedAccounts) => {
        const updatedAccounts = { ...prevSelectedAccounts };
        for (const group in popoverSelectedAccountsByGroup) {
          if (!updatedAccounts[group]) {
            updatedAccounts[group] = popoverSelectedAccountsByGroup[group];
          }
        }
        return updatedAccounts;
      });
    } catch (error) {
      message.error(errorToString(error));
    }
  };

  const editGroup = async (group) => {
    try {
      const updatedGroups = {
        ...popoverSelectedAccountsByGroup,
      };
      delete updatedGroups[UNGROUPED_NAME];
      // Ensure all group values are arrays
      Object.keys(updatedGroups).forEach((key) => {
        if (!Array.isArray(updatedGroups[key])) {
          updatedGroups[key] = [];
        }
      });
      // Remove selected accounts from other groups
      const selectedAccounts = new Set(updatedGroups[group] || []);
      Object.keys(updatedGroups).forEach((otherGroup) => {
        if (otherGroup !== group && Array.isArray(updatedGroups[otherGroup])) {
          updatedGroups[otherGroup] = updatedGroups[otherGroup].filter((account) => !selectedAccounts.has(account));
        }
      });
      // Remove empty groups
      Object.keys(updatedGroups).forEach((groupName) => {
        if (updatedGroups[groupName].length === 0 && groupName !== group) {
          delete updatedGroups[groupName];
        }
      });
      await Service.updateGroup(userInfo.email, updatedGroups);
      await fetchTradeStatusAndPermissions();
      message.success("Updated successfully");
    } catch (error) {
      message.error(errorToString(error));
    }
  };

  const addGroup = async (newGroupName) => {
    try {
      if (!newGroupName || newGroupName.length <= 0) {
        throw new Error("Group name can not be empty");
      }

      const updatedGroups = {
        ...popoverSelectedAccountsByGroup,
        [newGroupName]: popoverSelectedAccountsByGroup[UNGROUPED_NAME],
      };
      delete updatedGroups[UNGROUPED_NAME];

      await Service.updateGroup(userInfo.email, updatedGroups);
      await fetchTradeStatusAndPermissions();
      message.success("Updated successfully");
    } catch (error) {
      message.error(errorToString(error));
    }
  };

  const allParentExchangeAccounts = [
    ...new Set(
      Object.values(tradeStatusListByGroup)
        .flat()
        .filter((tradeStatus) => !isSubAccount(tradeStatus))
        .map((tradeStatus) => tradeStatus.exchangeAccount)
    ),
  ];

  const ungroupedParentExchangeAccountsSet = [
    ...new Set(
      tradeStatusListByGroup[UNGROUPED_NAME]
        ? tradeStatusListByGroup[UNGROUPED_NAME].filter((tradeStatus) => !isSubAccount(tradeStatus)).map((status) => status.exchangeAccount)
        : []
    ),
  ];

  const groupNames = Object.keys(tradeStatusListByGroup)
    .filter((groupName) => groupName !== UNGROUPED_NAME)
    .sort();
  if (tradeStatusListByGroup[UNGROUPED_NAME] && tradeStatusListByGroup[UNGROUPED_NAME].length > 0) {
    groupNames.push(UNGROUPED_NAME); // 如果有尚未分组的账户，则把这些账户放在最后
  }

  const showTradeModal = actionModalDetails && actionModalDetails.actionType;

  // sibling accounts are accounts using the same api keys.
  const otherSiblingAccountStatuses = [];
  if (actionModalDetails) {
    otherSiblingAccountStatuses.push(
      ...Object.values(tradeStatusListByGroup)
        .flat()
        .filter((tradeStatus) => {
          if (tradeStatus.exchangeAccount === actionModalDetails.exchangeAccount) {
            // the same account, not siblings
            return false;
          }
          if (tradeStatus.exchange !== actionModalDetails.exchange) {
            // not same exchange, not siblings
            return false;
          }
          return (
            (tradeStatus.parentAccount && tradeStatus.parentAccount === actionModalDetails.parentAccount) ||
            tradeStatus.account === actionModalDetails.parentAccount
          );
        })
    );
  }

  return (
    <div className={styles.accountOverview}>
      <Button
        onClick={() => setAllTablesExpanded(!allTablesExpanded)}
        icon={allTablesExpanded ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
        type="link"
      >
        {allTablesExpanded ? "Collapse All" : "Expand All"}
      </Button>
      {groupNames.map((group) => {
        const groupTradeStatusList = tradeStatusListByGroup[group];
        const hasAccountsWithPermissions = groupTradeStatusList.some(
          (status) => status.permissions.includes("READ") || status.permissions.includes("CONTROL")
        );

        if (!hasAccountsWithPermissions) {
          return null; // Don't render the GroupTable for this group
        }
        return (
          <GroupTable
            setSideBarPage={setSideBarPage}
            key={group}
            group={group}
            tradeStatusList={tradeStatusListByGroup[group]}
            setActionModalDetails={setActionModalDetails}
            popoverAllAccounts={allParentExchangeAccounts}
            popoverUngroupedAccounts={ungroupedParentExchangeAccountsSet}
            popoverSelectedAccounts={popoverSelectedAccountsByGroup[group]}
            onRemoveGroup={() =>
              setPopoverSelectedAccountsByGroup({
                ...popoverSelectedAccountsByGroup,
                [group]: [],
              })
            }
            onUpdateSelectedAccounts={(newSelectedAccounts) =>
              setPopoverSelectedAccountsByGroup({
                ...popoverSelectedAccountsByGroup,
                [group]: newSelectedAccounts,
              })
            }
            onConfirmEditGroup={() => editGroup(group)}
            onConfirmAddGroup={addGroup}
            allTablesExpanded={allTablesExpanded}
          />
        );
      })}
      {showTradeModal && (
        <TradeModal
          tradeModelDetails={actionModalDetails}
          siblingAccountStatuses={otherSiblingAccountStatuses}
          username={userInfo.username}
          closeModal={() => {
            setActionModalDetails({ ...actionModalDetails, actionType: undefined });
          }}
        />
      )}
    </div>
  );
};

export default AccountOverview;
